import React, { ChangeEvent, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import Input from '../Input';
import { AutocompleteItem, AutocompleteProps, DropdownProps } from './types';
import useClickOutside from '@/hooks/useClickOutside';

const DropdownItem = ({
  option,
  onSelect,
}: {
  option: string;
  onSelect: () => void;
}): JSX.Element => {
  return (
    <div
      aria-hidden
      data-testid="dropdown-item"
      onClick={onSelect}
      className="px-4 py-3 cursor-pointer text-on-gray-normal"
    >
      {option}
    </div>
  );
};

const DropdownList = ({ options, onSelect }: DropdownProps): JSX.Element => {
  const handleOptionSelect = (option: AutocompleteItem): void => {
    onSelect(option);
  };

  return (
    <div
      className={`absolute z-10 top-10 d:top-28 bg-white-normal w-full  rounded-xl border-outline-grey-muted ${
        options.length ? 'border' : ''
      }`}
    >
      {options.map((option, index) => {
        return (
          <React.Fragment key={index}>
            <DropdownItem
              option={option.name}
              onSelect={(): void => {
                handleOptionSelect(option);
              }}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
};

const AutoComplete = ({
  options,
  selected,
  onSelect,
  placeholder,
  showDefault = false,
  onChange,
}: AutocompleteProps): JSX.Element => {
  const [value, setValue] = useState(selected.name);
  const inputChanged = useRef(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const autoCompleteRef = useRef(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setValue(e.target.value);
    onChange(e.target.value);
    inputChanged.current = true;
    setShowDropdown(true);
  };

  const filteredOptions = useMemo(() => {
    if (!value) {
      return [];
    }

    const filtered = options.filter((option) =>
      option.name.toLowerCase().includes(value.toLowerCase()),
    );

    if (showDefault) {
      filtered.push({
        id: 'others',
        name: 'Others',
      });
    }

    return filtered;
  }, [value, options]);

  useEffect(() => {
    setValue(selected.name);
  }, [selected]);

  const handleSelect = (selectedValue: AutocompleteItem): void => {
    onSelect(selectedValue);
    setShowDropdown(false);
  };

  const onInputClear = (): void => {
    setValue('');
    onSelect({
      id: '',
      name: '',
    });
  };

  useClickOutside(
    autoCompleteRef,
    useCallback(() => {
      setShowDropdown(false);
    }, []),
  );

  return (
    <div ref={autoCompleteRef} className="relative d:static">
      <Input
        onClear={onInputClear}
        clearInput={Boolean(selected.name)}
        onChange={handleChange}
        value={value}
        iconBefore="search"
        placeholder={placeholder}
      />
      {showDropdown ? <DropdownList onSelect={handleSelect} options={filteredOptions} /> : null}
    </div>
  );
};

export default AutoComplete;
